/**
 * A React functional component that renders a dropdown select input for selecting a timezone.
 * It integrates with Redux for state management and supports validation and error handling.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {any} props.setting - The current setting object to be displayed.
 * @param {any[]} props.newSettings - The list of all settings, used for validation and error tracking.
 * @param {Function} props.onInputChange - Callback function triggered when the input value changes.
 * @param {string} props.productId - The ID of the product associated with the setting.
 * @param {any} props.settingKeyValidation - Validation logic for the setting key.
 *
 * @returns {JSX.Element} The rendered SelectComponent.
 */
import {
  Control,
  Label,
  Select,
  SelectOption,
  SelectOptionSearch,
  ControlError
} from "@forge/common";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { tenantActions } from "../../../store/Tenant/tenant-slice";
import { getGeoMapping } from "../../../store/Tenant/tenant-actions";
import "./SelectComponent.scss";
import { AppDispatch, RootState } from "../../../store";
import staticContent from "../../../utils/staticContent.json";

interface SelectComponentProps {
  setting: any;
  newSettings: any[];
  onInputChange: (
    event: any,
    setting: any,
    productId: string,
    errorMessage: string
  ) => void;
  productId: string;
  settingKeyValidation: any;
}

interface TimeZone {
  id: string;
  name: string;
}

interface SearchEvent {
  target: {
    value: string;
  };
}

const SelectComponent: React.FC<SelectComponentProps> = ({
  setting,
  newSettings,
  onInputChange,
  productId,
  settingKeyValidation,
}) => {
  const { geoMapping } = useSelector((state: RootState) => state.cart);
  const [timezoneList, setTimezoneList] = useState<TimeZone[]>([]);
  const [filteredTimezones, setFilteredTimezones] = useState<TimeZone[]>([]);
  const errorMessage = useRef(
    newSettings?.find((x: { key: any }) => x.key === setting.key)?.errorMessage
  );
  const [selectedTimezone, setSelectedTimezone] = useState(
    newSettings?.find((x: { key: any }) => x.key === setting.key)?.value ?? ""
  );
  const [selectedTimezoneId, setSelectedTimezoneId] = useState<string>("");
  const dispatch: AppDispatch = useDispatch();

  /**
   *
   * Fetches the geo mapping data when the component mounts.
   * This data is used to populate the timezone dropdown options.
   */
  useEffect(() => {
    dispatch(getGeoMapping());
  }, [dispatch]);

  /**
   *
   * Populates the timezone list and sets the default selected timezone
   */
  useEffect(() => {
    if (geoMapping) {
      const timezones = geoMapping?.regionDataMap
        ?.flatMap((obj: any) => obj.timezones)
        ?.filter(
          (timezone, index, self) =>
            index ===
            self.findIndex(
              (t) => t.id === timezone.id && t.name === timezone.name
            )
        );

      setTimezoneList(timezones);
      setFilteredTimezones(timezones);

      const defaultTimezone = timezones?.find(
        (timezone: TimeZone) => timezone.name === selectedTimezone
      );

      setSelectedTimezoneId(defaultTimezone?.id);
    }
  }, [geoMapping]);

  /**
   *
   * @param event
   * * Handles the change event for the select input.
   * * It validates the selected value and updates the error message accordingly.
   * * It also calculates the total error count for the settings and dispatches it to the Redux store.
   */
  const handleChange = (event: any) => {
    errorMessage.current = settingKeyValidation.onChange(event);

    // Calculate error count
    const errorCount = newSettings.reduce((count, otherSetting) => {
      // Add null/undefined check for otherSetting before accessing its properties
      if (!otherSetting) {
        return count;
      }
      
      if (otherSetting.key === setting.key) {
        return errorMessage.current?.length > 0 ? count + 1 : count;
      }
      return otherSetting?.errorMessage?.length > 0 ? count + 1 : count;
    }, 0);

    // Dispatch error count update
    dispatch(
      tenantActions.updateTabErrorCountMapping({
        tabName: productId,
        errorCount,
      } as any)
    );
  };

  /**
   *
   * @param event user input
   * * Handles the search event for filtering the timezone list based on user input.
   * * It updates the filtered timezone list based on the search value.
   */
  const handleSearch = (event: SearchEvent): void => {
    const searchValue = event.target.value.toLowerCase();
    const filteredTimezoneList = timezoneList.filter((timezone: TimeZone) =>
      timezone.name.toLowerCase().includes(searchValue)
    );

    setFilteredTimezones(filteredTimezoneList);
  };

  return (
    <Control className="control-select-component">
      <Label required>{setting.displayName}</Label>
      <Select
        aria-label={setting.displayName}
        className="select-component"
        height={2}
        id="product-setting-select"
        onChange={(event) => {
          setSelectedTimezoneId(event.target.value);
          const selectedTimezoneName = timezoneList.find(
            (timezone: TimeZone) => timezone.id === event.target.value
          )?.name;

          const newEvent = {
            target: {
              value: selectedTimezoneName,
            },
          };

          handleChange(newEvent);
          onInputChange(newEvent, setting, productId, errorMessage.current);
        }}
        placeholder={staticContent.selectTimezone}
        size="regular"
        value={selectedTimezoneId}
        search={
          <SelectOptionSearch
            label={staticContent.search}
            onChange={handleSearch}
            placeholder={staticContent.search}
            className="select-option-search"
            data-testid="select-option-search"
          />
        }
        data-testid="select-component"
      >
        {filteredTimezones?.map((timeZone: any) => (
          <SelectOption
            key={timeZone.id}
            value={timeZone.id}
            label={timeZone.name}
            data-testid="select-option"
          />
        ))}
      </Select>
      {errorMessage.current?.length > 0 && (
        <ControlError className="requiredText">
          {errorMessage.current}
        </ControlError>
      )}
    </Control>
  );
};

export default SelectComponent;
